import React, { useRef, useState } from 'react';
import { Box, useDisclosure } from '@chakra-ui/react';
import { useReactToPrint } from 'react-to-print';

import DataTable from '../../../common/certificateComponents/DataTable';
import CertificateBill from '../../../common/certificateComponents/CertificateBill';

import {
  CERTIFICATE_OF_PRODUCTION_TYPES,
  LOGS_TABLE_DISPLAY_SIZE,
} from '../../../../constants/certificateConstant';
import { useDispatch, useSelector } from 'react-redux';
import { renderDate } from '../../../../util/formatUtils';
import {
  deleteProductionCertPurchase,
  getProductionCertHistory,
} from '../../../../api/certificate';
import { BADGE_STATE_COLORS } from '../../../../constants/dataTableConstants';
import { FETCH_PRODUCTION_CERTIFICATE_DATA } from '../../../../store/actions/actionTypes';
import DialogueBox from '../../../common/DialogueBox';
import { notify } from '../../../../store/actions/globalActions';

//configure columns
const columns = [
  {
    header: 'Order Id',
    accessor: 'orderId',
  },
  {
    header: 'Date',
    accessor: 'orderDate',
    isDate: true,
  },
  {
    header: 'Range',
    accessor: 'certRange',
  },
  {
    header: 'Quantity',
    accessor: 'numOfCert',
  },
  {
    header: 'Type',
    accessor: 'type',
  },
  {
    header: 'Total',
    accessor: 'totalDue',
    currency: '৳',
  },
  {
    header: 'Payment',
    accessor: 'status',
    isBadge: true,
    colorScheme: BADGE_STATE_COLORS,
    hoverInfo: ['paymentMethod', 'chequeNo as ID', 'chequeDated as Date', 'remarks'],
  },
  {
    header: 'Bill',
    accessor: 'status',
    isButton: true,
    actionScheme: [
      { group: 'PAID', variant: 'print', action: 'PRINT' },
      { group: 'UNPAID', variant: 'print', action: 'PRINT' },
    ],
  },
  {
    header: 'Action',
    accessor: 'status',
    isButton: true,
    actionScheme: [{ group: 'UNPAID', variant: 'kill', action: 'DELETE' }],
  },
];

const PurchaseHistoryTable = () => {
  const dispatch = useDispatch();
  const certificateBillRef = useRef();
  const [rowData, setRowData] = useState();
  const [billData, setBillData] = useState();
  const [costData, setCostData] = useState();
  const mill = useSelector((state) => state.mill.mill);
  const data = useSelector((state) => state.certificate.productionCertData);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handlePagination = async ({ page, pageSize, searchKey, selected, startDate, endDate }) => {
    dispatch({ type: FETCH_PRODUCTION_CERTIFICATE_DATA, payload: null });
    const params = {
      page,
      pageSize,
      searchKey,
      type: selected?.['Certificate Type'],
      startDate,
      endDate,
    };
    const res = await getProductionCertHistory(params);
    if (res.data) {
      dispatch({ type: FETCH_PRODUCTION_CERTIFICATE_DATA, payload: res.data });
    }
  };

  const handleSearch = async ({ searchKey, selected, startDate, endDate }) => {
    dispatch({ type: FETCH_PRODUCTION_CERTIFICATE_DATA, payload: null });
    const params = { searchKey, type: selected?.['Certificate Type'], startDate, endDate };
    const res = await getProductionCertHistory(params);
    if (res.data) {
      dispatch({ type: FETCH_PRODUCTION_CERTIFICATE_DATA, payload: res.data });
    }
  };

  const printBill = useReactToPrint({
    documentTitle: `BTMA-Bill-Production-Certificate-${billData?.billNo}-${renderDate(new Date())}`,
    content: () => certificateBillRef.current,
  });

  const handlePrint = ({ rowObject }) => {
    setBillData({
      billNo: rowObject?.orderId,
      millName: mill?.millName,
      millAddress: mill?.hoInfo?.hoName,
    });
    setCostData({
      certOf: 'production',
      type: rowObject?.type,
      noOfCopies: rowObject?.numOfCert,
      unitCost: rowObject?.unitCost,
      total: rowObject?.totalDue,
      range: rowObject?.certRange,
    });
    setTimeout(() => {
      printBill();
    }, 500);
  };

  const handleDelete = ({ rowObject }) => {
    setRowData(rowObject);
    onOpen();
  };

  const confirmDelete = async () => {
    const res = await deleteProductionCertPurchase(rowData.orderId);
    if (res.data?.status === 200) {
      dispatch(
        notify({
          title: 'Success',
          description: res.data.msg,
          status: 'success',
          duration: 5000,
        }),
      );
      handlePagination({ page: 1, pageSize: 10 });
    }
  };

  const actionObject = { PRINT: handlePrint, DELETE: handleDelete };

  return (
    <Box>
      <DataTable
        keySearch
        dateRange
        isPaginated
        topPanel={true}
        columns={columns}
        search={handleSearch}
        actions={actionObject}
        currentPage={data?.page}
        caption="Purchase History"
        paginate={handlePagination}
        data={data?.purchaseHistory}
        entries={data?.totalEntries}
        currentPageSize={data?.pageSize}
        pageSizes={LOGS_TABLE_DISPLAY_SIZE}
        selectOptions={[CERTIFICATE_OF_PRODUCTION_TYPES]}
      />
      <Box display="none">
        <CertificateBill
          data={billData}
          costs={costData}
          title="Certificate"
          ref={certificateBillRef}
        />
      </Box>
      <DialogueBox
        close={onClose}
        alertTitle="DELETE"
        mountControl={isOpen}
        performAction={confirmDelete}
        alertMessage="Are you sure you want to delete this purchase request?"
      />
    </Box>
  );
};

export default PurchaseHistoryTable;
